import type { ChurchConfiguration } from "@lbcrm-api/church/church.responses";
import { PropsWithChildren } from "react";
import useSWR from "swr";
import { useAuth } from "src/authentication/hooks/useAuth";
import { LoadingScreen } from "src/core/components/LoadingScreen";
import { ChurchConfigurationContext } from "./ChurchConfigurationContext";

/** What a church does before it has said otherwise, so a failed load hides nothing */
const fallbackConfiguration: ChurchConfiguration = {
    consentFormsOffered: true,
};

/**
 * Waits for the configuration before rendering, so a section the church has
 * switched off never shows for a moment first. Sits above `ProtectedRoute`'s
 * `SWRConfig`, so it brings its own fetcher.
 */
export const ChurchConfigurationProvider = ({ children }: PropsWithChildren) => {
    const { person, client } = useAuth();

    const signedIn = person !== undefined && client !== undefined;

    const fetchConfiguration = async (url: string) => {
        if (!client) {
            throw new Error("Church configuration is only read signed in");
        }

        const { data } = await client.get<ChurchConfiguration>(url);

        return data;
    };

    const { data, error } = useSWR(signedIn ? "church/configuration" : null, fetchConfiguration);

    // A failed revalidation keeps what was last read rather than falling back
    const configuration = signedIn ? (data ?? (error ? fallbackConfiguration : null)) : fallbackConfiguration;

    if (configuration === null) {
        return <LoadingScreen img />;
    }

    return <ChurchConfigurationContext.Provider value={configuration}>{children}</ChurchConfigurationContext.Provider>;
};
